/**
 * Header & Collection AJAX Navigation
 * ============================================================
 * True client-side / AJAX navigation:
 *  - 0 full-page reloads when clicking header menu links or collection links,
 *    and collection/product links inside the search overlay (overlay closes on swap).
 *  - Content is fetched in background and swapped into #MainContent smoothly.
 *  - URL and browser History (Back/Forward) update correctly.
 *  - Header stays loaded (no destruction, no flicker).
 *  - Active menu link highlights update dynamically.
 *  - Mobile drawer immediately closes when a link inside it is clicked.
 *  - Instant transitions (< 100ms) powered by background prefetch & in-memory cache.
 *  - Non-blocking script execution (no redundant re-downloads or registry errors).
 *  - Safe fallback for cart, checkout, admin, external links.
 * ============================================================
 */
(function () {
  'use strict';

  // ── State ────────────────────────────────────────────────────
  var isNavigating = false;
  var pageCache = Object.create(null);
  var pageHtml = Object.create(null); // resolved HTML for synchronous swaps
  var loadedScripts = Object.create(null); // external script srcs already executed
  var styleLoads = Object.create(null); // section stylesheet href -> load promise (kept in <head>)

  // Paths that must never use AJAX
  var SKIP_PATHS = /^\/(cart|checkout|account|orders|gift_card|admin|a\/)/;

  // ── Helpers ──────────────────────────────────────────────────

  function isSafe(anchor) {
    if (!anchor || !anchor.href) return false;
    var raw = (anchor.getAttribute('href') || '').trim();
    if (!raw || /^(#|javascript:|mailto:|tel:)/.test(raw)) return false;
    var t = anchor.getAttribute('target');
    if (t && t !== '_self') return false;
    var url;
    try { url = new URL(anchor.href, location.origin); } catch (e) { return false; }
    if (url.origin !== location.origin) return false;
    if (SKIP_PATHS.test(url.pathname)) return false;
    if (/\.(pdf|zip|docx?|xlsx?|csv|exe|dmg)(\?|$)/i.test(url.pathname)) return false;
    return true;
  }

  function isHeaderLink(anchor) {
    if (!anchor) return false;
    var inHeader = anchor.closest('header, .header, .header-wrapper, [id^="shopify-section-"][id*="header"]');
    if (!inHeader) return false;

    // Match all menu links inside desktop header or mobile drawer
    if (anchor.closest(
      '.header__inline-menu, .mega-menu, dropdown-menu, header-drawer, #menu-drawer, .header__heading-link, .list-menu'
    )) {
      return true;
    }
    return false;
  }

  function isCollectionLink(anchor) {
    if (!anchor) return false;
    return !!anchor.closest('.collection-menu, .subcol-nav, .collection-list, .sub-collections, .banner__content a.button, .slideshow__box a');
  }

  // Collection & product links inside the search overlay (Popular, View all, product cards, live results)
  function isSearchLink(anchor) {
    if (!anchor || !anchor.closest('search-modal')) return false;
    var path = '';
    try { path = new URL(anchor.href, location.origin).pathname; } catch (e) { return false; }
    return /\/(collections|products)\//.test(path);
  }

  function isNavAnchor(anchor) {
    if (!anchor) return false;
    return isHeaderLink(anchor) || isCollectionLink(anchor) || isSearchLink(anchor);
  }

  // ── Mobile Sidebar Close ─────────────────────────────────────
  function closeMobileSidebar() {
    var headerDrawer = document.querySelector('header-drawer');
    if (headerDrawer) {
      try {
        headerDrawer.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: true, composed: true }));
      } catch (e) {}

      var details = headerDrawer.querySelector('details');
      if (details) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening', 'menu-closing');
      }
    }
    document.body.classList.remove('menu-mobile--open', 'menu-mobile--opening', 'menu-mobile--closing');
  }

  // ── Search Overlay Close ─────────────────────────────────────
  // Only #MainContent is swapped, so close the search overlay (in the header) and reset it
  // to the fresh state a full page load would show.
  function closeSearchOverlay() {
    var modals = document.querySelectorAll('search-modal');
    for (var i = 0; i < modals.length; i++) {
      var modal = modals[i];
      var details = modal.querySelector('details');
      if (!details || !details.hasAttribute('open') || typeof modal.close !== 'function') continue;

      modal.close();
      var predictive = modal.querySelector('predictive-search');
      if (predictive && typeof predictive.close === 'function') predictive.close(true);
      var content = modal.querySelector('.search-modal');
      if (content) content.classList.remove('searching');
    }
  }

  // ── Update Header Active State ───────────────────────────────
  function updateHeaderActiveLinks(url) {
    var path = '';
    try {
      path = new URL(url, location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (e) {
      return;
    }

    var isTopsSubCollection = (path === '/collections/baby-tee' || path === '/collections/shirts-tanks' || path.indexOf('/collections/t-shirt') !== -1);
    var isHoodiesSubCollection = (path === '/collections/zip-hoodies' || path.indexOf('zip-hoodie') !== -1);

    var headerLinks = document.querySelectorAll(
      '.header__inline-menu a, header-drawer a, #menu-drawer a, .mega-menu a, dropdown-menu a'
    );

    for (var i = 0; i < headerLinks.length; i++) {
      var a = headerLinks[i];
      var linkPath = '';
      try {
        linkPath = new URL(a.href, location.origin).pathname.replace(/\/$/, '') || '/';
      } catch (e) {
        continue;
      }

      var isDesktopTopItem = a.classList.contains('header__menu-item--top') && !!a.closest('.header__inline-menu');
      var linkText = (a.textContent || '').trim().toLowerCase();

      var isTopItemForTops = isDesktopTopItem && (linkText.indexOf('top') !== -1 || linkPath === '/collections/baby-tees');
      var isHoodiesItemForHoodies = isDesktopTopItem && (linkText.indexOf('hoodie') !== -1 || linkPath === '/collections/hoodies');

      var shouldBeBoldOnDesktop = (isTopsSubCollection && isTopItemForTops) || (isHoodiesSubCollection && isHoodiesItemForHoodies);

      var isActive = (linkPath === path);
      var isCollection = linkPath.indexOf('/collections/') !== -1 || (isDesktopTopItem && (linkPath === '/' || linkPath.indexOf('contact') !== -1));

      if (isActive || shouldBeBoldOnDesktop) {
        if (isActive) {
          a.classList.add('header__active-menu-item', 'menu-drawer__menu-item--active', 'list-menu__item--active');
          a.setAttribute('aria-current', 'page');
        }
        if (isCollection || shouldBeBoldOnDesktop) {
          a.classList.add('header__menu-item--collection-bold');
        } else {
          a.classList.remove('header__menu-item--collection-bold');
        }
      } else {
        a.classList.remove(
          'header__active-menu-item',
          'menu-drawer__menu-item--active',
          'list-menu__item--active',
          'header__menu-item--collection-bold'
        );
        a.removeAttribute('aria-current');
      }
    }
  }

  // ── Script Handling (Safe, non-blocking & deduplicated) ───────
  // A swapped-in page arrives after DOMContentLoaded/load already fired, so listeners its
  // scripts add for those events would never run (e.g. reviews slider, product video autoplay).
  // Collect them while the page's scripts run and call them once its new scripts have loaded,
  // the same order as a normal page load.
  function captureLoadListeners(target, queue) {
    var hadOwn = Object.prototype.hasOwnProperty.call(target, 'addEventListener');
    var previous = target.addEventListener;
    target.addEventListener = function (type, listener, options) {
      if (listener && (type === 'DOMContentLoaded' || (type === 'load' && target === window))) {
        queue.push({ target: target, type: type, listener: listener });
        return;
      }
      return previous.call(this, type, listener, options);
    };
    return function release() {
      if (hadOwn) target.addEventListener = previous;
      else delete target.addEventListener;
    };
  }

  function runScripts(container) {
    var scripts = container.querySelectorAll('script');
    var queue = [];
    var releaseDocument = captureLoadListeners(document, queue);
    var releaseWindow = captureLoadListeners(window, queue);
    var pending = 1; // the loop below + each new external script
    var finished = false;

    function done() {
      if (finished || --pending > 0) return;
      finished = true;
      releaseDocument();
      releaseWindow();
      queue.forEach(function (entry) {
        try {
          var event = new Event(entry.type);
          if (typeof entry.listener === 'function') entry.listener.call(entry.target, event);
          else if (typeof entry.listener.handleEvent === 'function') entry.listener.handleEvent(event);
        } catch (err) {
          console.error(err);
        }
      });
    }

    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];

      // External script (has src) — load only if it has never run on this page.
      // (Checking the DOM would match the swapped-in tag itself, so it never loaded.)
      if (old.src) {
        if (loadedScripts[old.src]) continue;
        loadedScripts[old.src] = true;

        var freshExternal = document.createElement('script');
        for (var j = 0; j < old.attributes.length; j++) {
          freshExternal.setAttribute(old.attributes[j].name, old.attributes[j].value);
        }
        if (!old.hasAttribute('async')) freshExternal.async = false; // keep document order, like defer
        pending++;
        freshExternal.onload = freshExternal.onerror = done;
        document.head.appendChild(freshExternal);
        continue;
      }

      // Inline script (no src) — re-execute for section initialization
      var freshInline = document.createElement('script');
      for (var k = 0; k < old.attributes.length; k++) {
        freshInline.setAttribute(old.attributes[k].name, old.attributes[k].value);
      }
      freshInline.textContent = old.textContent;
      try {
        old.parentNode.replaceChild(freshInline, old);
      } catch (err) {
        console.warn('Script execution notice:', err);
      }
    }

    done();
    // Never keep capturing if a script hangs
    setTimeout(function () { pending = 1; done(); }, 3000);
  }

  // Sync page-specific body classes (template-*, haar-<page title>) from the new page
  function syncBodyClass(newBody) {
    if (!newBody || !newBody.className) return;
    var pageClass = /^(template|haar)-/;
    var cur = document.body.className.split(/\s+/).filter(Boolean);
    cur = cur.filter(function (c) { return !pageClass.test(c); });
    newBody.className.split(/\s+/).forEach(function (c) {
      if (pageClass.test(c)) cur.push(c);
    });
    document.body.className = cur.join(' ');
  }

  // ── Promise-cached Fetch ─────────────────────────────────────
  function fetchPage(url) {
    var cleanUrl = url.split('#')[0];
    if (!pageCache[cleanUrl]) {
      pageCache[cleanUrl] = fetch(cleanUrl, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) {
            delete pageCache[cleanUrl];
            throw new Error('HTTP ' + res.status);
          }
          return res.text();
        })
        .then(function (html) {
          pageHtml[cleanUrl] = html;
          return html;
        })
        .catch(function (err) {
          delete pageCache[cleanUrl];
          throw err;
        });
    }
    return pageCache[cleanUrl];
  }

  // ── Background Prefetch for Instant Clicks ───────────────────
  function prefetchLinks() {
    var schedule = window.requestIdleCallback || function (cb) { setTimeout(cb, 200); };
    schedule(function () {
      var selector = '.header__inline-menu a, header-drawer a, .collection-menu a, .subcol-nav a, search-modal details[open] .search-recommendations a';
      var links = document.querySelectorAll(selector);
      for (var i = 0; i < links.length; i++) {
        var a = links[i];
        if (isSafe(a)) {
          var clean = a.href.split('#')[0];
          if (!pageCache[clean]) {
            fetchPage(clean);
          }
        }
      }
    });
  }

  // ── Stylesheets of the incoming page ────────────────────────
  // Sections load their CSS from inside #MainContent. Showing new content before that CSS has
  // loaded renders it unstyled for a moment (e.g. a huge close "X" icon, then a jerk), so load
  // any new render-blocking stylesheets first. Already-loaded ones resolve instantly.
  function loadStylesFor(newMain) {
    var links = newMain.querySelectorAll('link[rel="stylesheet"][href]');
    var waits = [];
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('media') === 'print') continue; // lazy-loaded by design
      var href = new URL(links[i].getAttribute('href'), location.href).href;
      if (!styleLoads[href]) {
        styleLoads[href] = new Promise(function (resolve) {
          var link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = link.onerror = function () { resolve(); };
          setTimeout(resolve, 2000); // never hold navigation on a slow stylesheet
          document.head.appendChild(link);
        });
      }
      waits.push(styleLoads[href]);
    }
    return Promise.all(waits);
  }

  function showPage(html, url, pushState) {
    var newDoc = new DOMParser().parseFromString(html, 'text/html');
    var newMain = newDoc.getElementById('MainContent') || newDoc.querySelector('main');
    if (!newMain) throw new Error('MainContent missing');
    return loadStylesFor(newMain).then(function () {
      applyPage(newDoc, url, pushState);
    });
  }

  // ── DOM Swap ─────────────────────────────────────────────────
  function applyPage(newDoc, url, pushState) {
    var newMain = newDoc.getElementById('MainContent') || newDoc.querySelector('main');
    var curMain = document.getElementById('MainContent') || document.querySelector('main');
    if (!newMain || !curMain) {
      // If MainContent container is not found, cannot do AJAX swap
      throw new Error('MainContent missing');
    }

    // Page body classes first, so the new content is laid out with its own page styles
    syncBodyClass(newDoc.body);

    // Swap main content immediately
    curMain.innerHTML = newMain.innerHTML;
    var attrs = newMain.attributes;
    for (var i = 0; i < attrs.length; i++) {
      curMain.setAttribute(attrs[i].name, attrs[i].value);
    }

    // Close the search overlay in the same frame as the swap (no flash of the old page)
    closeSearchOverlay();

    // Execute inline scripts safely
    runScripts(curMain);

    // Update document title & meta description
    if (newDoc.title) {
      document.title = newDoc.title;
    }
    var newMeta = newDoc.querySelector('meta[name="description"]');
    var curMeta = document.querySelector('meta[name="description"]');
    if (newMeta && curMeta) curMeta.setAttribute('content', newMeta.getAttribute('content'));
    if (newMeta && !curMeta) {
      var m = document.createElement('meta');
      m.name = 'description';
      m.content = newMeta.getAttribute('content');
      document.head.appendChild(m);
    }

    // Update header active links
    updateHeaderActiveLinks(url);

    // Update browser URL
    if (pushState !== false) {
      history.pushState({ ajaxNav: true, url: url }, newDoc.title, url);
    }

    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Notify theme components
    document.dispatchEvent(new CustomEvent('page:loaded'));
    document.dispatchEvent(new CustomEvent('collection:reloaded'));

    // Prefetch links on the new page
    prefetchLinks();
  }

  // ── Navigate ──────────────────────────────────────────────────
  function navigate(url, pushState) {
    var cleanCurrent = location.href.split('#')[0];
    var cleanTarget = url.split('#')[0];

    // If clicking link to current page, close sidebar / search overlay and exit
    if (cleanCurrent === cleanTarget && pushState !== false) {
      closeMobileSidebar();
      closeSearchOverlay();
      return;
    }

    if (isNavigating) return;
    isNavigating = true;

    // Immediately close mobile sidebar when clicked
    closeMobileSidebar();

    // Prefetched page: swap right away (same frame as the sidebar closing when its CSS is loaded)
    var ready = pageHtml[url.split('#')[0]];
    (ready ? Promise.resolve(ready) : fetchPage(url))
      .then(function (html) {
        return showPage(html, url, pushState);
      })
      .then(function () {
        isNavigating = false;
      })
      .catch(function (err) {
        console.warn('AJAX navigation error, falling back:', err);
        isNavigating = false;
        location.href = url; // graceful fallback
      });
  }

  // ── Event Handlers ───────────────────────────────────────────

  function onPointerPrefetch(event) {
    var anchor = event.target.closest('a');
    if (!anchor || !isSafe(anchor) || !isNavAnchor(anchor)) return;
    fetchPage(anchor.href);
  }

  function onDocumentClick(event) {
    var anchor = event.target.closest('a');
    if (!anchor) return;

    // Immediately close mobile drawer if any link inside it is clicked
    if (anchor.closest('header-drawer, #menu-drawer')) {
      closeMobileSidebar();
    }

    if (!isNavAnchor(anchor)) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (!isSafe(anchor)) return;

    event.preventDefault();
    navigate(anchor.href);
  }

  function onPopState(event) {
    var target = (event.state && event.state.url) ? event.state.url : location.href;
    navigate(target, false);
  }

  // ── Init ─────────────────────────────────────────────────────
  function init() {
    // Record scripts the initial page already ran
    for (var s = 0; s < document.scripts.length; s++) {
      if (document.scripts[s].src) loadedScripts[document.scripts[s].src] = true;
    }

    // Every link now uses a normal full page load (no AJAX page swap), so
    // Shopify Analytics and all pixels record every page view exactly like the
    // live theme. The old click interception, popstate handling and fetch()
    // prefetch are switched off on purpose (fetched HTML cannot be reused by a
    // real page load, so it only wasted requests).
    //
    // Speed: on desktop the browser prefetches the main header menu pages in
    // advance (Speculation Rules), so menu clicks open almost instantly. A
    // prefetch only downloads the HTML and never runs scripts, so it cannot
    // create fake page views. On phones Shopify's built-in prefetch (on tap /
    // hover) is used instead, to save mobile data. Browsers without support
    // simply load the page normally.
    addMenuPrefetchRules();

    function addMenuPrefetchRules() {
      try {
        if (!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'))) return;
        if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) return;
        var rules = document.createElement('script');
        rules.type = 'speculationrules';
        rules.textContent = JSON.stringify({
          prefetch: [{
            where: {
              and: [
                { selector_matches: '.header__inline-menu a' },
                { or: [
                  { href_matches: '/' },
                  { href_matches: '/(collections|pages|products|blogs|search){/*}?' }
                ] }
              ]
            },
            eagerness: 'eager'
          }]
        });
        document.head.appendChild(rules);
      } catch (e) {}
    }

    // Initial state sync for active collection bold highlight
    updateHeaderActiveLinks(location.href);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
